import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { Box, Checkbox, Typography } from '@mui/material';
import { useAccount } from 'wagmi';

import { useIsDisclaimerAccepted } from '@services/api/bonds/bonds.api';

import { authorizationActions } from '@contexts/common/AuthorizationContext';
import { modalActions } from '@contexts/common/ModalContext';

import { useAcceptBondsDisclaimer } from '@hooks/bonds/useAcceptBondsDisclaimer';

import Button from '@components/common/Button';
import ModalWrapper from '@components/common/Modal/ModalWrapper';

import { BOND_DISCLAIMER_PARAGRAPHS } from '@constants/bonds/disclaimer.constants';
import { Size, Variant } from '@constants/common/ui.constants';

import useStyles from './styles.module';

const BondsDisclaimerModal: FC = () => {
  const { classes } = useStyles();
  const [agreed, setAgreed] = useState(false);

  const { address } = useAccount();
  const { data: isAccepted } = useIsDisclaimerAccepted(address);
  const { accept, isPending } = useAcceptBondsDisclaimer();

  // Close on acceptance (201/409) or when the user disconnects — Bonds page
  // re-opens the modal once a new wallet connects and still needs to accept.
  useEffect(() => {
    if (isAccepted || !address) modalActions.hide();
  }, [isAccepted, address]);

  const handleToggle = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setAgreed(e.target.checked);
  }, []);

  return (
    <ModalWrapper title="Disclaimer" isHideCloseBtn disableBackdropClick>
      <Box className={classes.layout}>
        <Box className={classes.body}>
          {BOND_DISCLAIMER_PARAGRAPHS.map((text) => (
            <Typography
              key={text}
              variant="body2"
              color={(theme) => theme.palette.custom.text.light}
            >
              {text}
            </Typography>
          ))}
        </Box>
        <Box className={classes.checkboxRow}>
          {/* Colors come from the theme-level MuiCheckbox override (Sky DS) */}
          <Checkbox checked={agreed} onChange={handleToggle} />
          <Typography
            variant="body2"
            color={(theme) => theme.palette.custom.text.white}
          >
            Agree to terms
          </Typography>
        </Box>
        <Button
          variant={Variant.Secondary}
          size={Size.Large}
          className={classes.actionButton}
          disabled={isPending}
          onClick={authorizationActions.handleDisconnect}
          fullWidth
        >
          Disconnect Wallet
        </Button>
        <Button
          variant={Variant.Contained}
          size={Size.Large}
          disabled={!agreed || !address || isPending}
          isLoading={isPending}
          onClick={accept}
          className={classes.actionButton}
          fullWidth
        >
          Enter
        </Button>
      </Box>
    </ModalWrapper>
  );
};

export default BondsDisclaimerModal;
